import { useState, useEffect } from 'react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'login' | 'register';
}

export default function AuthModal({ isOpen, onClose, defaultTab = 'login' }: AuthModalProps) {
  const [tab, setTab] = useState<'login' | 'register'>(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    setTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setErrors({});
      setSubmitted(false);
      setSubmitError('');
      setFormData({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!isOpen) return null;

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (tab === 'register' && !formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (tab === 'register') {
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    return newErrors;
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    setSubmitError('');

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      if (formData.phone) body.append('phone', formData.phone);

      const response = await fetch('https://readdy.ai/api/form/d77an60rnr7vi0ujqnf0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setSubmitted(true);
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const switchTab = (newTab: 'login' | 'register') => {
    setTab(newTab);
    setErrors({});
    setSubmitted(false);
    setSubmitError('');
    setFormData({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop — full screen */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl w-full max-w-md overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'authModalIn 0.3s ease-out' }}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-saffron/10 to-forest/10 px-8 pt-8 pb-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-brown rounded-full hover:bg-white/60 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          <div className="flex items-center gap-3 mb-4">
            <img
              src="https://static.readdy.ai/image/3301efb4abc894ce1097fc48d2d46708/413245a79303f51f4a30352926bd751a.png"
              alt="Faasley"
              className="w-10 h-10 object-contain"
            />
            <div>
              <span className="font-display font-bold text-brown text-lg">Faasley</span>
              <span className="text-xs text-terracotta block -mt-0.5">Good Food Good Mood</span>
            </div>
          </div>

          {/* Tab Switcher */}
          <div className="flex bg-white/60 rounded-full p-1 gap-1">
            <button
              onClick={() => switchTab('login')}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                tab === 'login'
                  ? 'bg-saffron text-white'
                  : 'text-gray-500 hover:text-brown'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => switchTab('register')}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                tab === 'register'
                  ? 'bg-saffron text-white'
                  : 'text-gray-500 hover:text-brown'
              }`}
            >
              Create Account
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {submitted ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-forest text-3xl"></i>
              </div>
              <h3 className="font-display font-bold text-brown text-xl mb-2">
                {tab === 'register' ? 'Registration Successful!' : 'Welcome Back!'}
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                {tab === 'register'
                  ? 'Thanks for joining Faasley! We\'ve received your details. Our team will reach out to you soon with your account access.'
                  : 'You have been signed in successfully. Enjoy shopping with Faasley!'}
              </p>
              <button
                onClick={onClose}
                className="bg-saffron text-white font-semibold px-8 py-3 rounded-xl hover:bg-saffron-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Continue Shopping
              </button>
            </div>
          ) : tab === 'register' ? (
            /* ── REGISTRATION FORM (real submission) ── */
            <form
              data-readdy-form
              onSubmit={handleRegisterSubmit}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-brown mb-1.5">
                  Full Name
                </label>
                <div className="relative">
                  <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Enter your full name"
                    className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm text-brown placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-saffron/30 transition-all ${
                      errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'
                    }`}
                  />
                </div>
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-brown mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="Enter your email"
                    className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm text-brown placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-saffron/30 transition-all ${
                      errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-brown mb-1.5">
                  Phone Number <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <div className="relative">
                  <i className="ri-phone-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+91 98765 43210"
                    className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white text-sm text-brown placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-saffron/30 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-brown mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Create a password (min. 6 chars)"
                    className={`w-full pl-9 pr-10 py-3 rounded-xl border text-sm text-brown placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-saffron/30 transition-all ${
                      errors.password ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brown cursor-pointer"
                  >
                    <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-brown mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <i className="ri-lock-password-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    placeholder="Confirm your password"
                    className={`w-full pl-9 pr-10 py-3 rounded-xl border text-sm text-brown placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-saffron/30 transition-all ${
                      errors.confirmPassword ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brown cursor-pointer"
                  >
                    <i className={`${showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500 text-sm"></i>
                  <p className="text-red-600 text-xs">{submitError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-saffron hover:bg-saffron-dark text-white font-semibold py-3 rounded-xl transition-all duration-300 mt-2 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                    Creating Account...
                  </>
                ) : (
                  'Create My Account'
                )}
              </button>

              <p className="text-xs text-gray-400 text-center leading-relaxed">
                By creating an account, you agree to our{' '}
                <span className="text-saffron cursor-pointer hover:underline">Terms of Service</span>{' '}
                and{' '}
                <span className="text-saffron cursor-pointer hover:underline">Privacy Policy</span>
              </p>
            </form>
          ) : (
            /* ── SIGN IN FORM ── */
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2 mb-2">
                <i className="ri-information-line text-amber-500 text-sm mt-0.5"></i>
                <p className="text-amber-700 text-xs leading-relaxed">
                  Full login functionality coming soon. Register now to be among our first members!
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-brown mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="Enter your email"
                    className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm text-brown placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-saffron/30 transition-all ${
                      errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-brown mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Enter your password"
                    className={`w-full pl-9 pr-10 py-3 rounded-xl border text-sm text-brown placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-saffron/30 transition-all ${
                      errors.password ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brown cursor-pointer"
                  >
                    <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              <div className="text-right">
                <button type="button" className="text-saffron text-xs font-medium hover:underline cursor-pointer">
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                className="w-full bg-saffron hover:bg-saffron-dark text-white font-semibold py-3 rounded-xl transition-all duration-300 mt-2 cursor-pointer whitespace-nowrap"
              >
                Sign In to Faasley
              </button>

              <p className="text-xs text-gray-500 text-center">
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchTab('register')}
                  className="text-saffron font-semibold hover:underline cursor-pointer"
                >
                  Create one now
                </button>
              </p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        @keyframes authModalIn {
          from { opacity: 0; transform: scale(0.95) translateY(-10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
